import React, {
  useState,
  useEffect,
  useRef,
} from 'react';
import {
  Tab, Tabs, TabList, TabPanel,
} from 'react-tabs';
import { useTranslation } from 'react-i18next';
import '../../../../translations/i18n';
import { useForm } from 'react-hook-form';
import { Helmet } from 'react-helmet';
import AddressLookup from 'address-lookup';
import Modal from 'react-modal';
import useCountries from '../../../../hooks/useCountries';
import BankPayment from '../BankPayment/BankPayment';
import GiftCardPayment from '../GiftCardPayment/GiftCardPayment';
import styles from './PaymentDetails.module.scss';
import * as utils from '../../../../utils/OrderUtils';
import IdealPayment from '../IdealPayment/IdealPayment';
import Paypal from '../Paypal/Paypal';
import ApplePayTab from '../ApplePay/ApplePayTab';
import GooglePayTab from '../GooglePay/GooglePayTab';
import { getSiteConfig, getAppConfig } from '../../../../config/config';
import modalStyles from '../../../UI/Modal/Modal.module.scss';
import { ReactComponent as GooglePayIcon } from '../../../../svgs/google-pay-mark_800.svg';
import { ReactComponent as ApplePayIcon } from '../../../../svgs/applepay.svg';
import { ReactComponent as PaypalIcon } from '../../../../svgs/paypal.svg';
import DepositsCopy from './DepositsCopy';
import FlowPayment from '../FlowPayment/FlowPayment';

function hasNonEmptyValue(value) {
  return String(value || '').trim().length > 0;
}

function splitBillingName(name) {
  const [firstName = '', ...lastNameParts] = String(name || '').trim().split(/\s+/);

  return {
    firstName,
    lastName: lastNameParts.join(' '),
  };
}

function PaymentDetails(props) {
  const { t } = useTranslation('common');

  const {
    order, failureMessage, setFailureMessage, payAmount, flowPaymentEnabled,
  } = props;

  const config = getAppConfig();

  const cardholderName = order
    && utils.formatName(order.billing_address_fname, order.billing_address_lname);

  // current order to store new address if updated.
  const [currentOrder, setCurrentOrder] = useState(order);
  const [paymentAmount, setPaymentAmount] = useState(order && (payAmount || order.balance));
  const [name, setName] = useState(cardholderName);
  const [street1, setStreet1] = useState(
    order && order.billing_address_street1,
  );
  const [street2, setStreet2] = useState(
    order && order.billing_address_street2,
  );
  const [city, setCity] = useState(order && order.billing_address_city);
  const [region, setRegion] = useState(order && order.billing_address_region);
  const [country, setCountry] = useState(
    order && order.billing_address_country,
  );
  const [countryShortname, setCountryShortname] = useState('');
  const [postcode, setPostcode] = useState(
    order && order.billing_address_postal_code,
  );
  const [phone, setPhone] = useState(order && order.billing_address_phone);
  const hasRequiredBillingDetails = !!(
    order
    && hasNonEmptyValue(name)
    && hasNonEmptyValue(country)
    && hasNonEmptyValue(street1)
    && hasNonEmptyValue(city)
    && hasNonEmptyValue(postcode)
    && (!flowPaymentEnabled || hasNonEmptyValue(phone))
  );

  const address = order && {
    company: order.billing_address_company,
    street1,
    street2,
    city,
    region,
    postcode,
    country,
  };

  const billingAddress = address && Object.values(address).filter(Boolean).join(', ');

  const countriesList = useCountries();

  const [editState, setEditState] = useState(!hasRequiredBillingDetails);
  // checks if the form has been loaded before in the current page load
  // if not then it will reuse billing details and send it.
  // Because form is not loaded until the edit button is pressed.
  const [hasEditFormLoadedBefore, setHasEditFormLoadedBefore] = useState(!hasRequiredBillingDetails);
  const [giroPay] = useState(false);
  const [showIdealPay, setShowIdealPay] = useState(false);
  const [showPayPal] = useState(false);
  const [showApplePay, setShowApplePay] = useState(false);
  const [showGooglePay] = useState(true);
  const [showGiftTab] = useState(!(config.brand === 'UO' || config.brand === 'SW'));
  const [showStoreCard, setShowStoreCard] = useState(false);
  const [validAmount, setValidAmount] = useState(true);
  const [depositWeeks, setDepositWeeks] = useState();

  const lang = utils.getLang();

  const addressLookupAutocompleteRef = useRef(null);
  const addressLookupRef = useRef(null);
  const street1InputRef = useRef(null);

  useEffect(() => {
    if (flowPaymentEnabled) return;

    const siteConfig = getSiteConfig();
    if (
      siteConfig.idealPayEnabledLangs.includes(lang)
      && order.billing_address_country === siteConfig.idealPayEnabledCountry
    ) {
      setShowIdealPay(true);
    }

    // eslint-disable-next-line no-undef
    if (window.ApplePaySession && ApplePaySession.canMakePayments()) {
      setShowApplePay(true);
    }
  }, [flowPaymentEnabled, lang, order.billing_address_country]);

  const handleAddressUpdate = () => {
    setHasEditFormLoadedBefore(true);
    setEditState(!editState);
    // when edit form is closed clear ref
    if (editState) {
      addressLookupRef.current = null;
    }
  };

  const billingName = splitBillingName(name);
  const billingFormOrder = {
    ...currentOrder,
    billing_address_fname: billingName.firstName,
    billing_address_lname: billingName.lastName,
    billing_address_street1: street1,
    billing_address_street2: street2,
    billing_address_city: city,
    billing_address_region: region,
    billing_address_country: country,
    billing_address_postal_code: postcode,
    billing_address_phone: phone,
  };

  useEffect(() => {
    setCurrentOrder({
      ...currentOrder,
      billing_address_street1: street1,
      billing_address_street2: street2,
      billing_address_city: city,
      billing_address_region: region,
      billing_address_country: country,
      billing_address_postal_code: postcode,
      billing_address_phone: phone,
    });
    setCountryShortname(countryShortname);
    const isGooglePlacesReady = Boolean(window.google?.maps?.places?.Autocomplete);
    if (!addressLookupRef.current && street1InputRef.current && isGooglePlacesReady) {
      addressLookupRef.current = new AddressLookup(street1InputRef.current);
      const reactRoot = document.getElementById('cust-account');
      const countryEl = reactRoot.querySelector('[name="country"]');
      const countrySelectedOption = countryEl.options[countryEl.selectedIndex];

      // set initial country
      if (countrySelectedOption) {
        addressLookupRef.current.autoCompleteOptions.componentRestrictions = {
          country: countrySelectedOption.getAttribute('data-country-shortname'),
        };
      }

      // create an instance of autocomplete api
      addressLookupAutocompleteRef.current = addressLookupRef.current.init();
    }
  }, [
    street1,
    street2,
    city,
    region,
    country,
    postcode,
    phone,
    countryShortname,
    editState,
  ]);

  const addressLookup = addressLookupRef.current;
  const addressLookupAutocomplete = addressLookupAutocompleteRef.current;
  if (addressLookupAutocomplete) {
    if (countryShortname) {
      addressLookupAutocomplete.setOptions({
        componentRestrictions: { country: countryShortname },
      });
    }

    addressLookupAutocomplete.addListener('place_changed', () => {
      // when selecting an address from suggested list this is what it will do

      //  update the form values
      setValue('street1', addressLookup.getFullAddress().street1);
      setValue('street2', addressLookup.getFullAddress().street2);
      setValue('city', addressLookup.getFullAddress().city);
      setValue('region', addressLookup.getFullAddress().region);
      setValue('postcode', addressLookup.getFullAddress().postcode);
      setValue('country', addressLookup.getFullAddress().country);

      // update state
      setStreet1(addressLookup.getFullAddress().street1);
      setStreet2(addressLookup.getFullAddress().street2);
      setCity(addressLookup.getFullAddress().city);
      setRegion(addressLookup.getFullAddress().region);
      setPostcode(addressLookup.getFullAddress().postcode);
      setCountry(addressLookup.getFullAddress().country);

      // trigger validation after selecting an address
      trigger();
    });
  }

  const {
    setValue,
    trigger,
    register,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      name,
      country,
      street1,
      street2,
      city,
      region,
      postcode,
      phone,
    },
  });
  const { ref, ...rest } = register('street1', {
    required: true,
    onChange: (e) => {
      setStreet1(e.target.value);
      setValue('street1', e.target.value);
    },
  });
  const onSubmit = (data) => {};
  const isBillingDetailsValid = hasEditFormLoadedBefore ? isValid : hasRequiredBillingDetails;
  const shouldBlockFlowForBillingDetails = flowPaymentEnabled && !isBillingDetailsValid;

  useEffect(() => {
    if (editState && countriesList.countries.length > 0) {
      setValue('country', country, { shouldValidate: true });
      trigger();
    }
  }, [country, countriesList.countries.length, editState, setValue, trigger]);

  const deposit = order && order.deposit;
  const [isDepositAvailable, setIsDepositAvailable] = useState(true);
  const [minimumPaymentAmountRequired, setMinimumPaymentAmountRequired] = useState(0);
  const [isFullPaymentRequired, setIsFullPaymentRequired] = useState(false);
  const [isNonRefundableDeposit, setIsNonRefundableDeposit] = useState(!!deposit?.non_refundable_deposit?.amount);
  const [paymentOptions, setPaymentOptions] = useState([]);
  const [isCustomerPayingNonRefundableDeposit, setIsCustomerPayingNonRefundableDeposit] = useState(false);
  const cutoffDate = new Date(Date.parse(order.departure_date));
  cutoffDate.setDate(cutoffDate.getDate() - parseInt(42, 10));

  // Any non-zero logged payment means a deposit / part-payment was already captured;
  // the deposit vs full-payment choice should not be re-offered on subsequent attempts.
  const hasPriorPayment = Array.isArray(order?.order_payments)
    && order.order_payments.some((p) => Number(p.amount) > 0);

  useEffect(() => {
    if (deposit && Object.keys(deposit).length > 0) {
      if (hasPriorPayment) {
        setIsDepositAvailable(false);
        setIsFullPaymentRequired(false);
        setMinimumPaymentAmountRequired(20);
        setPaymentOptions([]);
        return;
      }

      const depositDays = 42;
      const depositWeek = (depositDays % 365) / 7;
      setDepositWeeks(depositWeek);
      const options = [];
      if (deposit.deposit_enabled && +deposit.qualify > 0) {
        const depositAmt = +deposit.deposittotal;
        setMinimumPaymentAmountRequired(depositAmt);

        const depStatus = ['quote', 'payment_failed', 'in_checkout'];
        if (depStatus.includes(order.status)) {
          if (cutoffDate < utils.getTimeInUTC()) {
            setIsFullPaymentRequired(true);
            setIsDepositAvailable(false);
            setMinimumPaymentAmountRequired(paymentAmount);
            options.push({
              id: 'full-payment',
              title: t('payment.full-payment'),
              price: order.balance,
              description: `${t('payment.full_amount_inside')} ${depositWeek} ${t('payment.full_amount_inside1')}`,
            });
            setPaymentOptions(options);
          } else {
            setIsDepositAvailable(true);

            if (isNonRefundableDeposit) {
              const nonRefAmount = deposit.non_refundable_deposit.amount;
              setPaymentAmount(nonRefAmount);
              setIsCustomerPayingNonRefundableDeposit(true);
              options.push({
                id: 'non-refundable',
                title: t('payment.non-refundable-deposit'),
                price: deposit.non_refundable_deposit.amount,
                description: t('payment.non-refundable-deposits-tab-message', { amount: utils.formatPrice(order.currency, deposit.non_refundable_deposit.amount) }),
              }, {
                id: 'refundable',
                title: t('payment.refundable-deposit'),
                price: deposit.deposittotal,
                description: t('payment.refundable-deposits-tab-message'),
              });
            } else {
              setPaymentAmount(deposit.deposittotal);
              options.push({
                id: 'deposit',
                title: t('Deposit'),
                price: deposit.deposittotal,
                description: null,
              });
            }

            options.push({
              id: 'full-payment',
              title: t('payment.full-payment'),
              price: order.balance,
              description: null,
            });

            setPaymentOptions(options);
          }
        } else {
          setMinimumPaymentAmountRequired(20);
          setIsDepositAvailable(false);
          if ((cutoffDate < utils.getTimeInUTC()) || (Number(order.balance) < 20)) {
            setIsFullPaymentRequired(true);
            setMinimumPaymentAmountRequired(paymentAmount);
            options.push({
              id: 'full-payment',
              title: t('payment.full-payment'),
              price: order.balance,
              description: Number(order.balance) < 20 ? t('payment.full_amount') : `${t('payment.full_amount_inside')} ${depositWeek} ${t('payment.full_amount_inside1')}`,
            });
            setPaymentOptions(options);
          }
        }
      } else {
        setMinimumPaymentAmountRequired(paymentAmount);
        setIsFullPaymentRequired(true);
        options.push({
          id: 'full-payment',
          title: t('payment.full-payment'),
          price: order.balance,
          description: t('payment.full_amount'),
        });
        setPaymentOptions(options);
      }
    }
  }, [deposit, hasPriorPayment]);

  const handlePaymentOptionChange = (e, id) => {
    setPaymentAmount(e.target.value);
    if (id === 'non-refundable') {
      setIsCustomerPayingNonRefundableDeposit(true);
    } else {
      setIsCustomerPayingNonRefundableDeposit(false);
    }
  };

  const [showModal, setShowModal] = useState(false);
  const [modalTitle, setModalTitle] = useState('');

  useEffect(() => {
    Modal.setAppElement(document.body);
  }, []);

  const handleModalClick = (paymentOption) => (e) => {
    e.preventDefault();
    setPaymentAmount(paymentOption.price);
    if (paymentOption.id === 'non-refundable') {
      setIsCustomerPayingNonRefundableDeposit(true);
    } else {
      setIsCustomerPayingNonRefundableDeposit(false);
    }
    setModalTitle(paymentOption.title);
    setShowModal(!showModal);
  };

  const handleModalClose = (e) => {
    e.preventDefault();
    setShowModal(!showModal);
  };

  // *** For future reference ***
  // if we save this form to database then consider the county field on the admin page is select field.

  const tabs = [
    flowPaymentEnabled ? { method: 'Flow' } : { method: 'Credit Card 3D' },
    !flowPaymentEnabled && showApplePay && { method: 'applepay' },
    !flowPaymentEnabled && showGooglePay && { method: 'googlepay' },
    !flowPaymentEnabled && showPayPal && { method: 'paypal' },
    showGiftTab && { method: 'Gift Card' },
    !flowPaymentEnabled && giroPay && { method: 'giropay' },
    !flowPaymentEnabled && showIdealPay && { method: 'ideal' },
  ].filter(Boolean);

  const latestPaymentMethod = order && order.order_payments && order.order_payments.length > 0
    ? order.order_payments[order.order_payments.length - 1].method
    : 'Credit Card 3D';

  const legacyTabIndex = tabs.findIndex((tab) => latestPaymentMethod === tab.method) !== -1
    ? tabs.findIndex((tab) => latestPaymentMethod === tab.method)
    : 0;

  const tabIndex = flowPaymentEnabled ? 0 : legacyTabIndex;

  return (
    <>
      <Helmet>
        <script
          async
          src="https://maps.googleapis.com/maps/api/js?key=AIzaSyADgxi3zb3O0_9CGeYLrYIfCLjg4csoAOw&libraries=places"
        />
      </Helmet>
      <div className={styles.paymentChunk}>
        {isDepositAvailable || isFullPaymentRequired ? (
          <div className={styles.paymentOptionsContainer}>
            <div className={styles.paymentOptionsList}>
              {paymentOptions.map((paymentOption) => {
                const isSelected = Number(paymentAmount) === Number(paymentOption.price);

                return (
                  <label
                    key={paymentOption.id}
                    htmlFor={`payment-${paymentOption.id}`}
                    className={`${styles.paymentCard} ${isSelected ? styles.isSelected : ''} ${paymentOptions.length > 1 ? styles.hoverPointer : ''}`}
                  >
                    <div className={styles.paymentCardHeader}>
                      <div className={styles.paymentCardLeft}>
                        {paymentOptions.length > 1 && (
                          <input
                            type="radio"
                            id={`payment-${paymentOption.id}`}
                            name="payment-option"
                            value={paymentOption.price}
                            checked={isSelected}
                            onChange={(e) => handlePaymentOptionChange(e, paymentOption.id)}
                            className={styles.paymentRadio}
                          />
                        )}
                        <span className={styles.paymentOptionName}>
                          {paymentOption.title}
                        </span>
                      </div>
                      <span className={styles.paymentPrice}>
                        {utils.formatPrice(order.currency, paymentOption.price)}
                      </span>
                    </div>

                    {paymentOption.description && (
                      <div className={styles.paymentCardFooter}>
                        <span>{paymentOption.description}</span>
                        {!isFullPaymentRequired && (
                          <a href="#modal" onClick={handleModalClick(paymentOption)}>
                            {' '}
                            Learn More
                          </a>
                        )}
                      </div>
                    )}
                  </label>
                );
              })}
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)}>
            <fieldset className={styles.fieldset}>
              <div className={styles.field}>
                <label className={styles.label} htmlFor="amount">
                  {t('payment.payment_form.amount')}
                  {' '}
                  <span title="Required" className={styles.required}>
                    {' '}
                    *
                  </span>
                  {!errors.amount && !validAmount && (
                  <span className={styles.error}>
                    {t('payment.depost_msg')}
                    {' '}
                    {utils.formatPrice(order.currency, minimumPaymentAmountRequired)}
                    {' '}
                    {t('payment.depost_msg1')}
                    {' '}
                    {utils.formatPrice(order.currency, order.balance)}
                    {' '}
                  </span>
                  )}
                </label>
                <input
                  className={styles.input}
                  type="number"
                  defaultValue={paymentAmount}
                  min={minimumPaymentAmountRequired}
                  max={order && order.balance}
                  {...register('amount', {
                    onChange: (e) => {
                      const { min, max, value } = e.target;

                      if (
                        Number(value) >= Number(min)
                      && Number(value) <= Number(max)
                      ) {
                        setPaymentAmount(value);
                        setValidAmount(true);
                        if (!validAmount) {
                          setValidAmount(true);
                        }
                      } else {
                        setValidAmount(false);
                        if (validAmount) {
                          setValidAmount(false);
                        }
                      }
                      setPaymentAmount(value);
                      if (Number(value) < Number(order.balance)) {
                        setShowStoreCard(true);
                      } else {
                        setShowStoreCard(false);
                      }
                    },
                    required: true,
                  })}
                  readOnly={
                    order && Number(order.balance) < 20 ? true : isFullPaymentRequired
                }
                />
                {(errors.amount || !paymentAmount) && (
                <p className={styles.error}>
                  {t('payment.payment_form.amount_err')}
                </p>
                )}
              </div>
            </fieldset>
          </form>
        )}
        <div className={styles.fieldset}>
          <div className={styles.field}>
            <h3 className={styles.addressLabel}>
              {t('account.billing_address')}
            </h3>
            <p className={`${styles.address} gb-mask`}>
              {billingAddress}
              {' '}
              <button
                className={styles.editLink}
                type="submit"
                onClick={handleAddressUpdate}
              >
                Edit
              </button>
            </p>
            {!editState
              && (hasEditFormLoadedBefore
                ? !isValid
                : !hasRequiredBillingDetails) && (
                <p className={styles.error}>
                  {t('payment.payment_form.err')}
                </p>
            )}
          </div>
          {editState && (
            <div className="customer-accounts-address gb-mask">
              <div className={styles.field}>
                <label className={styles.label} htmlFor="name">
                  {t('payment.payment_form.name')}
                  {' '}
                  <span title="Required" className={styles.required}>
                    *
                  </span>
                </label>
                <input
                  className={styles.input}
                  type="text"
                  defaultValue={name}
                  {...register('name', {
                    onChange: (e) => {
                      setName(e.target.value);
                    },
                    validate: hasNonEmptyValue,
                  })}
                />
                {errors.name && (
                  <p className={styles.error}>
                    {t('payment.payment_form.name_err')}
                  </p>
                )}
              </div>
              <div className={styles.field}>
                <label className={styles.label} htmlFor="country">
                  {t('payment.payment_form.country')}
                  {' '}
                  <span title="Required" className={styles.required}>
                    *
                  </span>
                </label>
                <select
                  className={styles.select}
                  value={country}
                  name="country"
                  {...register('country', {
                    onChange: (e) => {
                      setCountry(e.target.value);
                      const cShortname = e.target.options[
                        e.target.selectedIndex
                      ].getAttribute('data-country-shortname');
                      setCountryShortname(cShortname);
                    },
                    required: true,
                  })}
                >
                  {countriesList.countries.map((countries) => (
                    <option
                      data-country-shortname={countries.shortname}
                      key={countries.id}
                    >
                      {countries.title}
                    </option>
                  ))}
                </select>
                {errors.country && (
                  <p className={styles.error}>
                    {t('payment.payment_form.country_err')}
                  </p>
                )}
              </div>
              <div className={styles.field}>
                <label className={styles.label} htmlFor="street">
                  {t('payment.payment_form.street')}
                  {' '}
                  <span title="Required" className={styles.required}>
                    *
                  </span>
                </label>
                <input
                  {...rest}
                  ref={(e) => {
                    ref(e);
                    street1InputRef.current = e;
                  }}
                  name="street1"
                  id="street1"
                  className={`${styles.input} ${styles.inputFirst}`}
                  type="text"
                  defaultValue={street1}
                />
                <input
                  className={styles.input}
                  type="text"
                  onChange={(e) => setStreet2(e.target.value)}
                  defaultValue={street2}
                  {...register('street2', {
                    onChange: (e) => {
                      setStreet2(e.target.value);
                    },
                  })}
                />
                {errors.street1 && (
                  <p className={styles.error}>
                    {t('payment.payment_form.street_err')}
                  </p>
                )}
              </div>
              <div className={styles.field}>
                <label className={styles.label} htmlFor="city">
                  {t('payment.payment_form.city')}
                  {' '}
                  <span title="Required" className={styles.required}>
                    *
                  </span>
                </label>
                <input
                  className={styles.input}
                  type="text"
                  defaultValue={city}
                  {...register('city', {
                    onChange: (e) => {
                      setCity(e.target.value);
                    },
                    required: true,
                  })}
                />
                {errors.city && (
                  <p className={styles.error}>
                    {t('payment.payment_form.city_err')}
                  </p>
                )}
              </div>
              <div className={styles.field}>
                <label className={styles.label} htmlFor="region">
                  {t('payment.payment_form.county')}
                  {' '}
                </label>
                <input
                  className={styles.input}
                  type="text"
                  onChange={(e) => setRegion(e.target.value)}
                  defaultValue={region}
                  {...register('region', {
                    onChange: (e) => {
                      setRegion(e.target.value);
                    },
                  })}
                />
                {errors.region && (
                  <p className={styles.error}>
                    {t('payment.payment_form.county_err')}
                  </p>
                )}
              </div>
              <div className={styles.field}>
                <label className={styles.label} htmlFor="postcode">
                  {t('payment.payment_form.postcode')}
                  {' '}
                  <span title="Required" className={styles.required}>
                    *
                  </span>
                </label>
                <input
                  className={styles.input}
                  type="text"
                  defaultValue={postcode}
                  {...register('postcode', {
                    onChange: (e) => {
                      setPostcode(e.target.value);
                    },
                    required: true,
                  })}
                />
                {errors.postcode && (
                  <p className={styles.error}>
                    {t('payment.payment_form.postcode_err')}
                  </p>
                )}
              </div>
              <div className={styles.field}>
                <label className={styles.label} htmlFor="phone">
                  {t('payment.payment_form.phone')}
                  {' '}
                  {flowPaymentEnabled && (
                    <span title="Required" className={styles.required}>
                      *
                    </span>
                  )}
                </label>
                <span className={`${styles.inputWrap} validd errorr`}>
                  <input
                    className={styles.input}
                    type="text"
                    defaultValue={phone}
                    {...register('phone', {
                      onChange: (e) => {
                        setPhone(e.target.value);
                      },
                      required: flowPaymentEnabled,
                    })}
                  />
                </span>
                {errors.phone && (
                  <p className={styles.error}>
                    {t('payment.payment_form.phone_err')}
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
      <div className={styles.paymentChunk}>
        <Tabs key={`${flowPaymentEnabled ? 'flow' : 'legacy'}-${tabIndex}`} defaultIndex={tabIndex}>
          <TabList className={`${styles.tabList} ${styles.paymentTabList}`}>
            <Tab className={styles.tab}>
              {utils.isATBrand() && (
                <>
                  <svg width="10px">
                    <use xlinkHref="#tab-card" />
                  </svg>
                  {' '}
                </>
              )}
              {t('payment.payment_form.payment_details')}
            </Tab>
            {!flowPaymentEnabled && showApplePay && (
              <Tab className={styles.tabNoPadding}>
                <span className={styles.paymentLogo}><span className={styles.applepayIcon}><ApplePayIcon /></span></span>
              </Tab>
            )}
            {!flowPaymentEnabled && showGooglePay && (
              <Tab className={styles.tabNoPadding}>
                <span className={styles.paymentLogo}><span className={styles.gpayIcon}><GooglePayIcon /></span></span>
              </Tab>
            )}
            {!flowPaymentEnabled && showPayPal && (
              <Tab className={styles.tab}><span className={`${styles.paymentLogo} ${styles.paypal}`}><span><PaypalIcon /></span></span></Tab>
            )}
            {showGiftTab && (
              <Tab className={styles.tab}>
                {utils.isATBrand() && (
                  <>
                    <svg width="10px">
                      <use xlinkHref="#tab-gift" />
                    </svg>
                    {' '}
                  </>
                )}
                {t('payment.payment_form.giftcardtab')}
              </Tab>
            )}
            {!flowPaymentEnabled && giroPay && (
              <Tab className={styles.tab}>
                {t('payment.payment_form.giropaytab')}
              </Tab>
            )}
            {!flowPaymentEnabled && showIdealPay && (
              <Tab className={styles.tab}>
                {t('payment.payment_form.ideal')}
              </Tab>
            )}
          </TabList>
          <TabPanel className={styles.tabPanel}>
            {shouldBlockFlowForBillingDetails && (
              <div className={styles.billingNotice} role="status">
                <h3 className={styles.billingNoticeTitle}>
                  {t('payment.payment_form.billing_details_required')}
                </h3>
                <p className={styles.billingNoticeText}>
                  {t('payment.payment_form.billing_details_required_message')}
                </p>
              </div>
            )}
            {!shouldBlockFlowForBillingDetails && (
              flowPaymentEnabled ? (
                <FlowPayment
                  order={billingFormOrder}
                  payAmount={paymentAmount}
                  isBillingDetailsValid={isBillingDetailsValid}
                  isValidAmount={validAmount}
                  isCustomerPayingNonRefundableDeposit={isCustomerPayingNonRefundableDeposit}
                />
              ) : (
                <BankPayment
                  order={billingFormOrder}
                  payAmount={paymentAmount}
                  cardholderName={name}
                  isBillingDetailsValid={isBillingDetailsValid}
                  isShowStoreCard={showStoreCard}
                  isValidAmount={validAmount}
                  isCustomerPayingNonRefundableDeposit={isCustomerPayingNonRefundableDeposit}
                />
              )
            )}
          </TabPanel>
          {!flowPaymentEnabled && showApplePay && (
            <TabPanel>
              <ApplePayTab order={order} payAmount={paymentAmount} isCustomerPayingNonRefundableDeposit={isCustomerPayingNonRefundableDeposit} />
            </TabPanel>
          )}
          {!flowPaymentEnabled && showGooglePay && (
            <TabPanel>
              <GooglePayTab order={order} payAmount={paymentAmount} isCustomerPayingNonRefundableDeposit={isCustomerPayingNonRefundableDeposit} />
            </TabPanel>
          )}
          {!flowPaymentEnabled && showPayPal && (
            <TabPanel className={styles.tabPanel}>
              <Paypal order={currentOrder} payAmount={paymentAmount} />
            </TabPanel>
          )}
          {showGiftTab && (
            <TabPanel className={styles.tabPanel}>
              <GiftCardPayment order={currentOrder} isCustomerPayingNonRefundableDeposit={isCustomerPayingNonRefundableDeposit} />
            </TabPanel>
          )}
          {!flowPaymentEnabled && showIdealPay && (
            <TabPanel>
              <IdealPayment
                order={currentOrder}
                payAmount={paymentAmount}
                failureMessage={failureMessage}
                setFailureMessage={setFailureMessage}
              />
            </TabPanel>
          )}
        </Tabs>
      </div>
      <Modal
        isOpen={showModal}
        contentLabel={modalTitle}
        onRequestClose={handleModalClose}
        className={`${modalStyles.modal} ${styles.depositsModal}`}
        overlayClassName={modalStyles.modalOverlay}
      >
        <div className={styles.modalContainer}>
          <div className={styles.modalTitleContainer}>
            <h2>{modalTitle}</h2>
            <button className={styles.close} type="button" onClick={handleModalClose}>X</button>
          </div>
          <div className={modalStyles.leftText}>
            <DepositsCopy
              styles={styles}
              currency={order.currency}
              amount={paymentAmount}
              cutOffDate={cutoffDate}
              refundable={!isCustomerPayingNonRefundableDeposit}
              accommodationResortId={deposit.non_refundable_deposit?.accommodation_resort_id}
            />
          </div>
        </div>
      </Modal>
    </>
  );
}

export default PaymentDetails;
